import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, formatApiError, trackEvent } from "@/lib/api";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

export const DemoRequestSection = () => {
    const [email, setEmail] = useState("");
    const [company, setCompany] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        try {
            await api.post("/demo-request", {
                email: email.trim(),
                company: company.trim() || undefined,
            });
            trackEvent("demo_request_submit", { location: "landing" });
            setSent(true);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Something went wrong. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section id="request-demo" className="border-t border-border/60 scroll-mt-20" data-testid="demo-request-section">
            <div className="max-w-7xl mx-auto px-5 sm:px-10 py-16 lg:py-24">
                <div className="mono-label mb-4">Book a demo</div>
                <h2 className="font-display text-2xl sm:text-3xl max-w-2xl">
                    See it on your own engagements.
                </h2>
                <p className="mt-4 text-muted-foreground max-w-xl">
                    Leave your work email and we'll schedule a 30-minute walkthrough — no commitment, no card.
                </p>
                {sent ? (
                    <div
                        className="mt-8 inline-flex items-center gap-2 rounded-md border border-border bg-card px-4 py-3 text-sm"
                        data-testid="demo-request-success"
                    >
                        <CheckCircle2 className="h-4 w-4 status-pos shrink-0" strokeWidth={1.5} />
                        Thanks — we'll be in touch within one business day.
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-[1fr_14rem_auto] gap-3 max-w-2xl sm:items-end">
                        <div>
                            <label htmlFor="demo-email" className="block text-sm font-medium mb-1.5">
                                Work email
                            </label>
                            <Input
                                id="demo-email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@firm.com"
                                className="h-[52px]"
                                data-testid="demo-request-email-input"
                            />
                        </div>
                        <div>
                            <label htmlFor="demo-company" className="block text-sm font-medium mb-1.5">
                                Company (optional)
                            </label>
                            <Input
                                id="demo-company"
                                type="text"
                                value={company}
                                onChange={(e) => setCompany(e.target.value)}
                                placeholder="Firm name"
                                className="h-[52px]"
                                data-testid="demo-request-company-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={busy || !email.trim()}
                            className="h-[52px] rounded-md font-display px-6 w-full"
                            data-testid="demo-request-submit-btn"
                        >
                            {busy ? (
                                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                            ) : (
                                <>
                                    Book a demo
                                    <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                                </>
                            )}
                        </Button>
                    </form>
                )}
            </div>
        </section>
    );
};
